// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {ALL, INVENTORIES} from 'Ui/components/graph/constants/graph-filters';
import {formatLocalizedTime} from 'Utils/time-utils';
import moment from 'moment';
import DataGridWrapper from 'Ui/components/common/data-table/data-grid-wrapper';

function getDataGridCostColumns(secondarySelector, translations) {
    const equipmentColumnsSection = secondarySelector === ALL ? [] : [
        {
            headerName: translations.BRAND,
            field: 'brand',
            minWidth: 150,
            flex: 1
        },
        {
            headerName: translations.MODEL,
            field: 'model',
            minWidth: 150,
            flex: 1
        },
        {
            headerName: translations.ONLINK_JOINED,
            field: 'joined',
            minWidth: 150,
            flex: 1,
            valueFormatter: (value) => value ? formatLocalizedTime(moment(value).toDate(), {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            }) : ''
        }
    ];

    return [
        {
            headerName: translations.NAME,
            field: 'name',
            minWidth: 200,
            flex: 1
        },
        ...equipmentColumnsSection,
        {
            headerName: translations.ONLINK_PURCHASE,
            field: 'purchaseCost',
            minWidth: 150,
            flex: 1,
            type: 'number',
            renderCell: (params) => params.row.purchaseCostFormatted
        },
        {
            headerName: translations.ONLINK_TAX,
            field: 'tax',
            minWidth: 150,
            flex: 1,
            type: 'number',
            renderCell: (params) => params.row.taxFormatted
        },
        {
            headerName: translations.PARTS,
            field: 'parts',
            minWidth: 150,
            flex: 1,
            type: 'number',
            renderCell: (params) => params.row.partsFormatted
        },
        {
            headerName: translations.ONLINK_SERVICE,
            field: 'service',
            minWidth: 150,
            flex: 1,
            type: 'number',
            renderCell: (params) => params.row.serviceFormatted
        },
        {
            headerName: translations.ONLINK_TOTAL,
            field: 'total',
            minWidth: 150,
            flex: 1,
            type: 'number',
            renderCell: (params) => params.row.totalFormatted
        },
        {
            headerName: translations.HOURS,
            field: 'engineHours',
            minWidth: 150,
            flex: 1,
            type: 'number',
            renderCell: (params) => params.row.engineHoursFormatted
        },
        {
            headerName: translations.ONLINK_COST_PER_HOUR,
            field: 'costPerEngineHour',
            minWidth: 150,
            flex: 1,
            type: 'number',
            renderCell: (params) => params.row.costPerEngineHourFormatted
        }
    ];
}

function getDataGridInventoriesColumns(translations) {
    return [
        {
            headerName: translations.ONLINK_PART_TYPE,
            field: 'partType',
            minWidth: 200,
            flex: 1
        },
        {
            headerName: translations.ONLINK_TOTAL_COST,
            field: 'totalCost',
            minWidth: 150,
            flex: 1,
            type: 'number',
            renderCell: (params) => params.row.formattedTotalCost
        }
    ];
}

function getDataGridColumns(primarySelector, secondarySelector, translations) {
    if (primarySelector === INVENTORIES) {
        return getDataGridInventoriesColumns(translations);
    }

    return getDataGridCostColumns(secondarySelector, translations);
}

function LifetimeReportDataTable(props) {
    const {
        primarySelector,
        secondarySelector,
        tableData,
        translations
    } = props;

    return (
        <div className={'ops-center-table-container'}>
            <DataGridWrapper
                columns={getDataGridColumns(primarySelector, secondarySelector, translations)}
                getRowId={(row) => primarySelector === INVENTORIES ? row.partType : row.name}
                pinnedColumns={{
                    left: [],
                    right: []
                }}
                rows={tableData}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
        </div>
    );
}

LifetimeReportDataTable.propTypes = {
    primarySelector: PropTypes.string,
    secondarySelector: PropTypes.string,
    tableData: PropTypes.arrayOf(PropTypes.object),
    translations: PropTypes.translations

};

export default LifetimeReportDataTable;
